import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import Swal from 'sweetalert2'
import {GET, PUT} from '../../../../services/api'
import {Input} from '../../../../components/Input/Input'
import {Loading} from '../../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../../translate/i18n'
import {DragDropContext, Droppable, Draggable, DropResult} from 'react-beautiful-dnd'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import usePluginsSpace from '../../../../hooks/usePluginSpace'
import {hhmmToIsoToday, hhmmToSeconds, intervalValue, isoToHhmm} from './hooks/dateHour'

const EditarGradesHotelContent: FC = () => {
  const navigate = useNavigate()
  const {id} = useParams()
  const {useTotvs} = usePluginsSpace()
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState('')
  const [variation, setVariation] = useState('')
  const [status, setStatus] = useState<boolean>(false)
  const [hourInit, setHourInit] = useState<string | undefined>(undefined)
  const [hourEnd, setHourEnd] = useState<string | undefined>(undefined)
  const [interval, setInterval] = useState<string | undefined>(undefined)
  const [list, setList] = useState<string[]>([])
  const [disableFieldsTotvs, setDisableFieldTotvs] = useState(false)

  const addList = () => {
    const newList = list
    newList.unshift(variation)
    setVariation('')
    setList(newList)
  }

  const removeList = (variationPosition: number) => {
    const newList = list.filter((variationFiltered, index) => variationPosition !== index)
    setVariation('')
    setList(newList)
  }

  const editGrid = async () => {
    try {
      if (!title || list.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: i18n.t(`service_grid.missing_fields`) as string,
          text: i18n.t(`service_grid.missing_fields_message`) as string,
        })
      } else {
        setLoading(true)
        const gridRes = await PUT(`/grid-hotel/${id}`, {
          title,
          content: {config: list},
          status,
          startHour: hourInit ? hhmmToIsoToday(hourInit) : null,
          endHour: hourEnd ? hhmmToIsoToday(hourEnd) : null,
          interval: interval ? hhmmToSeconds(interval) : null,
        })
        setLoading(false)
        if (gridRes.success === true) {
          Swal.fire({
            icon: 'success',
            title: i18n.t(`service_grid.grid_updated`) as string,
            text: i18n.t(`service_grid.grid_updated_message`) as string,
          }).then(() => navigate('/hospitality/structured-content/grid-hotel'))
        } else {
          Swal.fire({
            icon: 'error',
            title: i18n.t(`service_grid.error`) as string,
            text: i18n.t(`service_grid.error_update_message`) as string,
          })
        }
      }
      return
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t(`service_grid.error`) as string,
        text: i18n.t(`service_grid.error_update_message`) as string,
      })
    }
  }

  const getGrid = async () => {
    try {
      setLoading(true)
      if (useTotvs) {
        setDisableFieldTotvs(true)
      }
      const gridRes = await GET(`/grid-hotel/${id}`)
      setTitle(gridRes.title)
      setStatus(gridRes.status)
      setHourInit(isoToHhmm(gridRes.startHour))
      setHourEnd(isoToHhmm(gridRes.endHour))
      setInterval(intervalValue(gridRes.interval))
      const content =
        typeof gridRes.content === 'string' ? JSON.parse(gridRes.content) : gridRes.content
      const listVariation: string[] = content.config
      setList(listVariation)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const onDragEnd = (res: DropResult, list: any, setList: any) => {
    if (!res.destination) return

    const {source, destination} = res
    const copiedItems = [...list]

    const [removed] = copiedItems.splice(source.index, 1)

    copiedItems.splice(destination.index, 0, removed)

    setList(copiedItems)
  }

  useEffect(() => {
    getGrid()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t(`service_grid.edit_service_grid`)}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t(`service_grid.save_changes_edit`)}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='card-body border-bottom'>
                <div className='d-flex justify-content-between align-items-center flex-wrap gap-5'>
                  <div className='flex-grow-1'>
                    <Input
                      inputAttr={{
                        type: 'string',
                      }}
                      placeholder={i18n.t(`service_grid.service_grid_name_placeholder`)}
                      label={i18n.t(`service_grid.service_grid_name`)}
                      change={(value: string) => setTitle(value)}
                      value={title}
                      disabled={disableFieldsTotvs}
                      onlyLetters={true}
                      marginTop='0'
                      maxCaracteres={30}
                    />
                  </div>
                  <div className='flex-grow-1'>
                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      value={status}
                      change={setStatus}
                      label={i18n.t(`general.active`)}
                      options={[
                        {select: i18n.t(`general.active`), value: true},
                        {select: i18n.t(`general.inactive`), value: false},
                      ]}
                      marginTop='0'
                    />
                  </div>
                </div>
                <div className='d-flex align-items-center gap-5 flex-wrap'>
                  <Input
                    inputAttr={{type: 'hour'}}
                    placeholder={i18n.t(`service_grid.start_hour_placeholder`)}
                    label={i18n.t(`service_grid.start_hour`)}
                    change={(value: string) => setHourInit(value)}
                    value={hourInit ?? ''}
                    marginTop='0'
                  />
                  <Input
                    inputAttr={{type: 'hour'}}
                    placeholder={i18n.t(`service_grid.end_hour_placeholder`)}
                    label={i18n.t(`service_grid.end_hour`)}
                    change={(value: string) => setHourEnd(value)}
                    value={hourEnd}
                    marginTop='0'
                  />
                  <Input
                    inputAttr={{type: 'hour'}}
                    placeholder={i18n.t(`service_grid.interval_placeholder`)}
                    label={i18n.t(`service_grid.interval`)}
                    change={(value: string) => setInterval(value)}
                    value={interval}
                    toolTip={true}
                    toolTipIcon={'info-circle'}
                    toolTiptext={i18n.t(`service_grid.interval_tooltip`)}
                    marginTop='0'
                  />
                </div>
                <div className='form-group mt-5'>
                  <label className='form-label fw-normal'>
                    {i18n.t(`service_grid.service_list`)}
                  </label>
                  <div className='input-group'>
                    <input
                      type='text'
                      className='form-control fw-normal'
                      placeholder={i18n.t(`service_grid.service_placeholder`)}
                      value={variation}
                      onChange={(event) => {
                        setVariation(event.target.value)
                      }}
                      disabled={disableFieldsTotvs}
                    />
                    <div className='input-group-append'>
                      <button
                        className='btn btn-success'
                        style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}
                        type='button'
                        onClick={() => addList()}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <DragDropContext onDragEnd={(result) => onDragEnd(result, list, setList)}>
                  <Droppable droppableId='ROOT' type='group'>
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {list.map((variationListed, index) => (
                          <Draggable key={index} draggableId={index.toString()} index={index}>
                            {(provided) => (
                              <div
                                className='form-group mt-5'
                                key={index}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className='input-group'>
                                  <button className='btn btn-sm btn-dark' disabled>
                                    <i className='bi bi-arrows-move'></i>
                                  </button>
                                  <input
                                    type='text'
                                    className='form-control fw-normal'
                                    placeholder={i18n.t(`service_grid.service_placeholder`)}
                                    value={variationListed}
                                    onChange={(event) => {
                                      const newList = [...list]
                                      newList[index] = event.target.value
                                      setList(newList)
                                    }}
                                    disabled={disableFieldsTotvs}
                                  />
                                  <div className='input-group-append'>
                                    <button
                                      disabled={disableFieldsTotvs}
                                      className='btn btn-danger'
                                      style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}
                                      type='button'
                                      onClick={() => removeList(index)}
                                    >
                                      -
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
              <div className='d-flex w-100'>
                <div className='col-6 d-flex justify-content-start'>
                  <Button
                    text={i18n.t(`buttons.back`)}
                    color='info'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => navigate(-1)}
                    btnClass='fw-bolder m-9'
                  />
                </div>
                <div className='col-6  d-flex justify-content-end'>
                  <Button
                    text={i18n.t(`buttons.save`)}
                    color='primary'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => editGrid()}
                    btnClass='fw-bolder m-9'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const EditarGradesHotel: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t(`service_grid.dashboard`) as string, path: 'dashboard', isActive: true},
          {title: i18n.t(`service_grid.service_grid`) as string, path: 'grid', isActive: true},
          {title: i18n.t(`general.edit`) as string, path: 'grid/editar', isActive: true},
        ]}
      >
        {i18n.t(`service_grid.service_grid`)}
      </PageTitle>
      <EditarGradesHotelContent />
    </>
  )
}

export {EditarGradesHotel}
